import {
	ACCOUNT_HEALTH_PERCENT_CRITICAL,
	ACCOUNT_HEALTH_PERCENT_WARNING,
} from 'src/constants/constants';
import { getColorVar } from 'src/utils/theme/colorVars';
import { SetOptional } from 'type-fest';

type ClassNameType = 'fill' | 'text' | 'bg' | 'color-var';
type ClassNameMap = Record<Status, string>;
type Status = 'critical' | 'warning' | 'healthy' | 'fallback';

interface OptionsWithColorVarType {
	type: 'color-var';
	map?: never;
}

interface OptionsWithNonColorVarType {
	type: Exclude<ClassNameType, 'color-var'>;
	map?: never;
}
interface OptionsWithMap {
	/**
	 * If present, returns the given mapped value instead of the default.
	 * If `fallback` is not provided, `''` is returned for its case.
	 */
	map: SetOptional<ClassNameMap, 'fallback'>;
	type?: never;
}

type Options =
	| OptionsWithColorVarType
	| OptionsWithNonColorVarType
	| OptionsWithMap;

export default function healthDependentClassName(
	health: number | undefined,
	{ type, map }: Options
) {
	const classNameMap = map ?? CLASS_NAME_MAP_BY_TYPE[type];

	if (health === undefined) {
		return classNameMap.fallback ?? '';
	}

	if (health < ACCOUNT_HEALTH_PERCENT_CRITICAL) {
		return classNameMap.critical;
	}

	if (health < ACCOUNT_HEALTH_PERCENT_WARNING) {
		return classNameMap.warning;
	}

	return classNameMap.healthy;
}

const CLASS_NAME_MAP_BY_TYPE: Record<ClassNameType, ClassNameMap> = {
	fill: {
		fallback: 'fill-text-default',
		critical: 'fill-text-negative-red-button',
		warning: 'fill-star-yellow',
		healthy: 'fill-text-positive-green-button',
	},
	text: {
		fallback: 'text-text-default',
		critical: 'text-text-negative-red-button',
		warning: 'text-star-yellow',
		healthy: 'text-text-positive-green-button',
	},
	bg: {
		// Not sure what this should be yet.
		fallback: '',
		critical: 'bg-text-negative-red-button',
		warning: 'bg-star-yellow',
		healthy: 'bg-text-positive-green-button',
	},
	'color-var': {
		fallback: getColorVar('text-default'),
		critical: getColorVar('text-negative-red-button'),
		warning: getColorVar('star-yellow'),
		healthy: getColorVar('text-positive-green-button'),
	},
};
